import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { PurchaseOrder } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { useGetPurchaseOrder } from 'src/services/purchaseOrder.service';
import PurchaseOrderActionMenuButton from 'src/components/ActionButtons/PurchaseOrderActionMenu';
import { loadData } from 'src/utils/grid.utils';
import { errorMessages } from 'src/utils/properties.utils';
import { fi } from 'date-fns/locale';

const PurchaseOrderGrid: FC<{
  state: boolean;
  searchTerm: string;
  filters: {
    vendorId: string;
    status: string;
    startDate: Date | null;
    endDate: Date | null;
  };
}> = ({ state, searchTerm, filters }) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const {
    loading,
    purchaseOrders,
    totalItems,
    fetchPurchaseOrders,
    deletePurchaseOrder,
  } = useGetPurchaseOrder();
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);

  useEffect(() => {
    fetchPurchaseOrders(page, pageSize, searchTerm, sortBy, order, filters);
  }, [page, pageSize, searchTerm, sortBy, order, state, filters]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    setPage(1);
    setSortBy(newSortModel[0]?.field || 'createdAt');
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.15,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 0.5,
    },
    { field: 'vendor', headerName: 'Vendor', flex: 1.8 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <PurchaseOrderActionMenuButton
          rowId={String(params.id)}
          id={params.row.id}
          status={params.row.status}
          fetchPurchaseOrders={() =>
            fetchPurchaseOrders(
              page,
              pageSize,
              searchTerm,
              sortBy,
              order,
              filters
            )
          }
          deletePurchaseOrder={deletePurchaseOrder}
        />
      ),
    },
  ];

  const transformData = (purchaseOrders: PurchaseOrder[]) => {
    try {
      return purchaseOrders.map((purchaseOrder, index) => ({
        id: purchaseOrder.id,
        number: (page - 1) * pageSize + index + 1,
        createdAt: new Date(purchaseOrder.createdAt).toLocaleDateString(
          'en-GB'
        ),
        vendor: purchaseOrder.vendor.organizationName,
        deliveryAddress: purchaseOrder.deliveryAddress,
        status: purchaseOrder.status,
      }));
    } catch (error) {
      showSnackbar(
        errorMessages.ERROR_TRANSFORMING_PURCHASE_ORDER_DATA,
        'error'
      );
      return [];
    }
  };

  const rows = transformData(purchaseOrders);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default PurchaseOrderGrid;
